import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  Box,
  MenuItem,
  InputAdornment,
  Autocomplete,
  Stack,
  CircularProgress,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import CategoryIcon from "@mui/icons-material/Category";
import GroupsIcon from "@mui/icons-material/Groups";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PublicIcon from "@mui/icons-material/Public";
import LanguageIcon from "@mui/icons-material/Language";
// import TargetIcon from '@mui/icons-material/Target';  // double-check if you're using the right icon name
import InfoIcon from "@mui/icons-material/Info";
import ReceiptIcon from "@mui/icons-material/ReceiptLong";

import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import FormHelperText from "@mui/material/FormHelperText";
import { getCountries } from "react-phone-number-input";
import typography from "../../cssUtils/typography";
import { ColorsConstants } from "../../cssUtils/constants";
import { GET_TENANT_ONBOARDING_OPTIONS_COMPANY_DETAILS } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";

const CompanyDetailsForm = ({ companyData, errors, onChange }) => {
  const { getValidToken } = useAuth();

  const [onboardingOptions, setOnboardingOptions] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(true);

  useEffect(() => {
    getOnboardingOptions();
  }, []);

  const getOnboardingOptions = async () => {
    const headers = {
      "Content-Type": "application/json",
    };

    const url = GET_TENANT_ONBOARDING_OPTIONS_COMPANY_DETAILS;

    try {
      const response = await fetch(url, { headers });

      if (!response.ok) throw new Error("Failed to fetch data");

      const data = await response.json();

      setOnboardingOptions(data);
    } catch (error) {
      console.error("Error fetching onboarding options: ", error);
    } finally {
      setOptionsLoading(false);
    }
  };

  const countries = getCountries().map((countryCode) =>
    new Intl.DisplayNames(["en"], { type: "region" }).of(countryCode),
  );

  const states = [
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chhattisgarh",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jammu and Kashmir",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
  ];

  const primaryFocusOptions = [
    "B2B (Business To Business)",
    "B2C (Business To Consumer)",
    "Both B2B and B2C",
  ];
  const hearAboutUsOptions = [
    "Search Engine",
    "Social Media",
    "Friends/Colleague",
    "Advertisement",
  ];

  if (optionsLoading) {
    return (
      <Box
        sx={{
          w: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "300px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Company Details
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Please provide information about your company.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            id="companyName"
            label="Company Name"
            name="companyName"
            value={companyData.companyName}
            onChange={(e) => onChange("companyName", e.target.value)}
            error={!!errors.companyName}
            helperText={errors.companyName}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            select
            id="industry"
            label="Industry"
            name="industry"
            value={companyData.industry}
            onChange={(e) => onChange("industry", e.target.value)}
            error={!!errors.industry}
            helperText={errors.industry}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CategoryIcon color="action" />
                </InputAdornment>
              ),
            }}
          >
            {onboardingOptions?.industry_options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            select
            id="size"
            label="Company Size"
            name="size"
            value={companyData.size}
            onChange={(e) => onChange("size", e.target.value)}
            error={!!errors.size}
            helperText={errors.size}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <GroupsIcon color="action" />
                </InputAdornment>
              ),
            }}
          >
            {onboardingOptions?.company_size_options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            id="website"
            label="Company Website"
            name="website"
            value={companyData.website}
            error={!!errors.website}
            helperText={errors.website}
            onChange={(e) => onChange("website", e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LanguageIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            fullWidth
            options={countries}
            value={companyData.country || null}
            onChange={(event, newValue) => onChange("country", newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label="Company Location"
                error={!!errors.country}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <PublicIcon color="action" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Stack
                {...props}
                key={option}
                gap={0.5}
                sx={{
                  alignItems: "flex-start !important",
                  textAlign: "left",
                  width: "100%",
                }}
              >
                <Typography>{option}</Typography>
                <Typography
                  sx={{
                    ...typography.bodySmall,
                    color: ColorsConstants.primaryGray40,
                  }}
                >
                  Billing Currency: {option === "India" ? "INR" : "USD"}
                </Typography>
              </Stack>
            )}
          />
        </Grid>
        {companyData.country === "India" && (
          <Grid item xs={12} sm={6}>
            <TextField
              required={companyData.country === "India"}
              fullWidth
              select
              id="state"
              label="Company State"
              name="state"
              value={companyData.state}
              onChange={(e) => onChange("state", e.target.value)}
              error={!!errors.country}
              helperText={errors.country}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PublicIcon color="action" />
                  </InputAdornment>
                ),
              }}
            >
              {states.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <TextField
            // Only required for India
            // required={companyData.country === 'India'}
            fullWidth
            id="gst"
            label={`GST${companyData.country === "India" ? " (optional)" : ""}`}
            name="gst"
            value={companyData.gst}
            onChange={(e) => onChange("gst", e.target.value)}
            error={!!errors.gst}
            helperText={errors.gst}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ReceiptIcon color="action" />
                </InputAdornment>
              ),
            }}
            inputProps={{ maxLength: 15 }}
            // Disable the field when country is not India
            disabled={companyData.country !== "India"}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControl
            component="fieldset"
            error={!!errors.primaryFocus}
            sx={{ mt: 1 }}
          >
            <FormLabel component="legend">
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <GroupsIcon color="action" sx={{ mr: 1 }} />
                Primary Focus *
              </Box>
            </FormLabel>
            <RadioGroup
              row
              name="primaryFocus"
              value={companyData.primaryFocus}
              onChange={(e) => onChange("primaryFocus", e.target.value)}
            >
              {primaryFocusOptions.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio />}
                  label={option}
                />
              ))}
            </RadioGroup>
            {errors.primaryFocus && (
              <FormHelperText>{errors.primaryFocus}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl
            component="fieldset"
            error={!!errors.hearAboutUs}
            sx={{ mt: 1 }}
          >
            <FormLabel component="legend">
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <InfoIcon color="action" sx={{ mr: 1 }} />
                How did you hear about us? *
              </Box>
            </FormLabel>
            <RadioGroup
              row
              name="hearAboutUs"
              value={companyData.hearAboutUs}
              onChange={(e) => onChange("hearAboutUs", e.target.value)}
            >
              {hearAboutUsOptions.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio />}
                  label={option}
                />
              ))}
            </RadioGroup>
            {errors.hearAboutUs && (
              <FormHelperText>{errors.hearAboutUs}</FormHelperText>
            )}
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CompanyDetailsForm;
